import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from "recharts";
import { Users, ScrollText, Ticket, Tag, Store, TrendingUp } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, testId }) => (
  <div className="card-sharp p-6 fade-up" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="label-tiny">{label}</div>
        <div className="text-4xl font-light mt-3" style={{ fontFamily: "Outfit" }}>{value}</div>
      </div>
      <div className="w-10 h-10 rounded-sm bg-[#E5DAC8] flex items-center justify-center">
        <Icon size={16} strokeWidth={1.5} className="text-[#6B5634]" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return (
    <Layout title="Dashboard" subtitle="Today">
      <div className="text-[#8C8C8C]">Loading…</div>
    </Layout>
  );

  const k = data.kpis;
  return (
    <Layout title="Dashboard" subtitle="Overview" data-testid="dashboard-page">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <Kpi icon={Users} label="Customers" value={k.total_customers} testId="kpi-customers" />
        <Kpi icon={ScrollText} label="Bills Today" value={k.bills_today} testId="kpi-bills-today" />
        <Kpi icon={Ticket} label="Vouchers Claimed" value={k.claims_total} testId="kpi-claims" />
        <Kpi icon={Tag} label="Active Offers" value={k.active_offers} testId="kpi-offers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-sharp p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-tiny">14-day Trend</div>
              <div className="text-xl mt-1" style={{ fontFamily: "Outfit" }}>Signups & Bills</div>
            </div>
            <TrendingUp size={16} strokeWidth={1.5} className="text-[#C5A880]" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.trend}>
              <CartesianGrid stroke="#F0EEE9" />
              <XAxis dataKey="day" stroke="#8C8C8C" fontSize={11} />
              <YAxis stroke="#8C8C8C" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", borderRadius: 2, color: "#fff" }} />
              <Line type="monotone" dataKey="signups" stroke="#C5A880" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="bills" stroke="#1A1A1A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card-sharp p-6">
          <div className="label-tiny mb-1">Top Brands</div>
          <div className="text-xl mb-6" style={{ fontFamily: "Outfit" }}>By Claims</div>
          {data.top_brands.length === 0 ? (
            <div className="text-[#8C8C8C] text-sm">No claims yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.top_brands} layout="vertical">
                <XAxis type="number" stroke="#8C8C8C" fontSize={11} hide />
                <YAxis dataKey="brand" type="category" stroke="#8C8C8C" fontSize={11} width={90} />
                <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", borderRadius: 2, color: "#fff" }} />
                <Bar dataKey="claims" fill="#C5A880" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <div className="card-sharp p-6">
          <div className="label-tiny mb-2">Active Brands</div>
          <div className="text-3xl font-light" style={{ fontFamily: "Outfit" }}>{k.active_brands}</div>
          <div className="text-[12px] text-[#8C8C8C] mt-3 flex items-center gap-2">
            <Store size={12} strokeWidth={1.5} /> Across mall floors
          </div>
        </div>
        <div className="card-sharp p-6">
          <div className="label-tiny mb-2">Total Bills</div>
          <div className="text-3xl font-light" style={{ fontFamily: "Outfit" }}>{k.bills_total}</div>
        </div>
        <div className="card-sharp p-6 bg-[#1A1A1A] text-white">
          <div className="label-tiny mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>WhatsApp Bot</div>
          <div className="text-xl font-light" style={{ fontFamily: "Outfit" }}>Karix WABA</div>
          <div className="text-[12px] text-white/60 mt-3">+91 8130790443</div>
          <div className="text-[11px] text-white/40 mt-1">Live • Powered by Fundle</div>
        </div>
      </div>
    </Layout>
  );
}
